import { useParams } from "react-router-dom";
import { OlympiadExamConstants } from "../../common/data/olympiadExams";
import { IOlympiadExam } from "../../common/models/olympiadExam";
import { Box, Card, CardContent, Chip, Divider, Grid, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { IOEFileDeleteRequest, IOlympiadExamFileRequest, IOlympiadExamProps } from "../../schoolPrograms/olympiadExam/olympiadExamModels";
import { toast } from "react-toastify";
import { IOlympiadExamFile } from "./olympiadExamModels";
import { LoadingButton } from "@mui/lab";

import moment from "moment";
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OlympiadExamService from "./olympiadExamService";
import ProgramFileUploadComponent from "../programFileUpload/programFileUpload";
import { FileTypeIcon } from "../../common/components/FileTypeIcon";
import { getFileNameWithOutExt } from "../../common/utilities/utils";

export const OlympiadExam = (props: IOlympiadExamProps) => {
    const { isAddFilesVisible, isDeleteFilesVisible } = props;
    const { examName } = useParams();
    const [olympiadExam, setOlympiadExam] = useState<IOlympiadExam | undefined>();
    const [olympiadExamFiles, setOlympiadExamFiles] = useState<IOlympiadExamFile[]>([]);
    const [fileUploadIsInProgress, setFileUploadIsInProgress] = useState<boolean>(false);
    const [filesLoading, setFilesLoading] = useState<boolean>(false);

    useEffect(() => {
        if (examName) {
            const exam = OlympiadExamConstants.find(i => i.examName === examName);
            setOlympiadExam(exam);

            if (exam) {
                setFilesLoading(true);
                OlympiadExamService.getFiles(exam.examId).then((result) => {
                    if (result.data.errors && result.data.errors.length > 0) {
                        toast.error(result.data.errors[0].message);
                    } else {
                        setOlympiadExamFiles(result.data.list);
                    }
                }).finally(() => setFilesLoading(false));
            } else {
                setOlympiadExam(undefined);
                toast.error("Exam not found");
            }
        } 
    }, [examName]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!olympiadExam) {
            toast.error("Exam not found");
            return;
        }

        const uploadedFiles = Array.from(e.target.files || []);
        setFileUploadIsInProgress(true);
        OlympiadExamService.fileUpload(uploadedFiles, { examId: olympiadExam.examId } as IOlympiadExamFileRequest)
            .then((response) => {
                if (response.data.errors !== undefined && response.data.errors !== null && response.data.errors.length > 0) {
                    toast.error("error in uploading the files");
                } else {
                    setOlympiadExamFiles([...olympiadExamFiles, ...response.data.list]);
                }
            }).finally(() => {
                setFileUploadIsInProgress(false);
            });
    }

    const handleDownloadClick = (programFile: IOlympiadExamFile) => {
        const fileDownloadUrl = OlympiadExamService.getFileDownloadUrl(programFile.fileId);
        window.open(fileDownloadUrl, '_blank');
    }

    const handleFileDelete = (fileDetails: IOlympiadExamFile) => {
        if (!window.confirm(`Delete "${getFileNameWithOutExt(fileDetails.fileName)}"?`)) return;
        const programFiles = olympiadExamFiles;
        const fileIndex = programFiles.findIndex(i => i.fileId === fileDetails.fileId);
        programFiles[fileIndex] = { ...fileDetails, isDeleteInProgress: true };
        setOlympiadExamFiles(programFiles);

        OlympiadExamService.deleteFile({ fileId: fileDetails.fileId } as IOEFileDeleteRequest)
            .then((response) => {
                if (response.data.errors !== undefined && response.data.errors !== null && response.data.errors.length > 0) {
                    toast.error("error in deleting the files");
                } else {
                    const programFilesData = olympiadExamFiles.filter(i => i.fileId !== fileDetails.fileId);
                    setOlympiadExamFiles([...programFilesData]);
                    toast.success("file deleted successfully");
                }
            });
    }

    if (!olympiadExam) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
                <Typography color="text.secondary">Select an exam from the menu.</Typography>
            </Box>
        );
    }

    return (
        <Grid container sx={{ height: "100%" }}>
            <Grid item xs={12}>
                <Card sx={{ height: "100%" }}>
                    {/* Page header */}
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {olympiadExam.examName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                Exam Files &amp; Resources
                            </Typography>
                        </Box>
                        <Chip
                            label={`${olympiadExamFiles.length} file${olympiadExamFiles.length !== 1 ? 's' : ''}`}
                            variant="outlined"
                            size="small"
                            color="default"
                        />
                    </Box>
                    <Divider />
                    {filesLoading && <LinearProgress />}
                    <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                        {isAddFilesVisible && (
                            <ProgramFileUploadComponent
                                handleFileChange={handleFileUpload}
                                fileUploadIsInProgress={fileUploadIsInProgress}
                            />
                        )}
                        <TableContainer sx={{
                            width: "100%",
                            maxHeight: "70vh",
                            minHeight: "70vh",
                            overflowY: "auto",
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="files table" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>File Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Uploaded Date</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', bgcolor: 'grey.50' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {olympiadExamFiles.length === 0 && !filesLoading && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography color="text.secondary" sx={{ py: 4 }}>No files uploaded yet.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {olympiadExamFiles.map((programFile) => (
                                        <TableRow key={programFile.fileId} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <FileTypeIcon fileName={programFile.fileName} />
                                                    <Typography sx={{ fontSize: '0.875rem' }}>
                                                        {getFileNameWithOutExt(programFile.fileName)}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                                {moment(programFile.createdDate).format('DD-MMM-YYYY HH:mm')}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={1}>
                                                    <LoadingButton
                                                        loading={programFile.isDeleteInProgress}
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<CloudDownloadIcon />}
                                                        loadingPosition="start"
                                                        onClick={() => handleDownloadClick(programFile)}
                                                    >
                                                        Download
                                                    </LoadingButton>
                                                    {isDeleteFilesVisible && (
                                                        <LoadingButton
                                                            loading={programFile.isDeleteInProgress}
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            startIcon={<DeleteIcon />}
                                                            loadingPosition="start"
                                                            onClick={() => handleFileDelete(programFile)}
                                                        >
                                                            Delete
                                                        </LoadingButton>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}